/* eslint-disable no-console */
import React, { useState, useContext } from 'react';
import classnames from 'classnames';
import compact from 'lodash/compact';
import assign from 'lodash/assign';
import reduce from 'lodash/reduce';
import chunk from 'lodash/chunk';
import { ArrowRight, ArrowLeft } from 'react-feather';
import { useIntl } from 'react-intl';
import CloudWatchLogs from 'aws-sdk/clients/cloudwatchlogs';
import { PageContext } from '../PageContext';
import { v4tov3base } from '../../lib/mapV4toV3';
import { v4 as uuidv4 } from 'uuid';
import * as styles from './ContactForm.module.scss';

type intlObj = { id: string, defaultMessage: string }
type InputProps = { label?: intlObj; name: string; placeholder?: intlObj; type: string, pattern?: string, required: boolean };
type ContactForm = {
  title?: intlObj;
  text?: intlObj;
  selectOptions?: { label: intlObj; value: string }[];
  inputs?: InputProps[];
  fullInputs: InputProps[];
  textArea?: InputProps;
  submit?: { placeholder: intlObj };
  checkBox?: InputProps;
  className?: string;
  showFormInFooter?: boolean;
  isTestPage?: boolean;
  associateFunction?: Function | null;
};

const ContactForm = ({ title,
  text = {} as intlObj,
  fullInputs = [],
  inputs,
  textArea = {} as InputProps,
  checkBox = {} as InputProps,
  className,
  showFormInFooter = false,
  associateFunction,
  submit = {
    placeholder: { id: 'send', defaultMessage: 'Send' }
  },
  isTestPage = false,
}: ContactForm) => {
  const initial = reduce([...fullInputs], function (memo, current) { return assign(memo, { [current.name]: '' }) }, { 'Extra': isTestPage ? 'Test' : 'Internal form' })
  const [data, setData] = useState({ ...initial });
  const [showbutton,setshowbutton] = useState(false);
  const [success, setSuccess] = useState('');
  const intl = useIntl();

  const { websiteLocale } = useContext(PageContext);
  const { hasRightToLeftLanguage } = v4tov3base({ ...websiteLocale.attributes });

  const updateData = e => {
    setData({
      ...data,
      [e.target.name]: e.target.value
    })
  }

  const createloginCloudwatch = async (status) => {
    const cloudwatchlogs = new CloudWatchLogs({
      accessKeyId: process.env.GATSBY_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.GATSBY_AWS_SECRET_ACCESS_KEY,
      region: process.env.GATSBY_AWS_DEFAULT_REGION
    });
    // Define the log group and log stream names
    const logGroupName = "isreal-form-submission";
    const logStreamName = "log-form-submit";
    try {
      // Log a message to CloudWatch
      cloudwatchlogs.putLogEvents({
        logGroupName,
        logStreamName,
        logEvents: [
          {
            message: JSON.stringify({ "name": data.FullName, "phone_number": data.phone, 'API Status': status, 'Form type': data.Extra }),
            timestamp: new Date().getTime(),
          },
        ],
      }, (err, data) => {
        if (err) {
          console.error("error sending log event:", err);
        } else {
          console.log("Log event sent:", data);
        }
      });
    } catch (error) {
      console.log("error in creating logs", error);
    }
  }

  async function fetchWithRetry (url, maxRetries = 3, retryDelay = 2000) {
    let retries = 0;
    var statuscode;
    while (retries < maxRetries) {
      try {
        const response = await fetch(url);  
        // Request was successful, return the response
        if (!response.ok) {
          statuscode = response.status
          throw new Error(`Request failed with status: ${response.status}`);
        }  
        const responseText = await response.text();
        return {status:response.status,message:responseText};
      } catch (error) {
        console.error(`Attempt ${retries + 1} failed:`, error);
        // Increment the retry counter
        retries++;
        // If there are more retries remaining, wait and then retry
        if (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
  
   return{status:statuscode,message:`Max retries (${maxRetries}) exceeded. Unable to fetch data.`};
  }

  const handleSubmit = async () => {
    const uuid = uuidv4();
    const url = reduce(data, (string, val, key) => (`${string}&${key}=${val}`), `https://wshr.acc.co.il/wsws.aspx?key=${process.env.GATSBY_FORM_API_KEY}&id=${uuid}`)
    try {
     setshowbutton(true);
     const res = await fetchWithRetry(url);
      if (res.status === 200) {
        createloginCloudwatch({ 'status code': res.status, "message": res.message, "uuid":uuid });
        setData(initial);
        setSuccess(intl.formatMessage({ id: 'success-message', defaultMessage: 'Thanks for your interest. A member of our team will contact you.' }))
        if (associateFunction) {
          associateFunction()
        }
        const dataObject = {
          'event': 'gtm.formSubmit',
          'gtm.elementId': 'contact-form-submit',
          'category': 'click',
          'label': isTestPage ? 'Test' : 'Internal form',
        };
        if (typeof dataLayer != 'undefined') {
          dataLayer.push(dataObject);
        }
      } else {
        createloginCloudwatch({ 'status code': res.status, "message": res.message, "uuid":uuid });
        const dataObject = {
          'event': 'gtm.formSubmit',
          'gtm.elementId': 'contact-form-fail',
          'category': 'click',
          'label': isTestPage ? 'Test' : 'Internal form',
        };
        if (typeof dataLayer != 'undefined') {
          dataLayer.push(dataObject);
        }
      }
    } catch (err) {
      createloginCloudwatch(err);
    }
     finally{
      setshowbutton(false);
     }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
      <div className={styles.contactForm}>
        <header className={classnames(styles.formHeader, className)}>
          {title?.id ? <h2 className={classnames('title color-a mb-1', className)}>{intl.formatMessage({ id: title?.id, defaultMessage: title?.defaultMessage })}</h2> : <></>}
          {text?.id ? <p className="is-text-two">{intl.formatMessage({ id: text.id, defaultMessage: text.defaultMessage })}</p> : <></>}
        </header>
        {
          success ? <div className={styles.success}>
            <p>{success}</p>
          </div> :
            <>
              <div className={classnames(styles.mainBody, className)}>
                {compact(fullInputs).map((item, index) => (
                  <div key={index} className={classnames(styles.requiredField, "is-secondary is-fullwidth mb-4")}>
                    {item.label?.id ? <label className={classnames('label', styles.customLabel)} htmlFor={item.name}>
                      {intl.formatMessage({ id: item.label.id, defaultMessage: item.label.defaultMessage })}
                    </label> : <></>}
                    <input pattern={item?.pattern ?? null} onChange={updateData} value={data[item.name]} name={item.name} className="input" type="text" required={item.required} placeholder={intl.formatMessage({ id: item.placeholder?.id, defaultMessage: item.placeholder?.defaultMessage })} />
                  </div>
                ))}
                {chunk(inputs, 2).map(([left, right], index) => (
                  <div className="columns" key={index}>
                    <div className={classnames(styles.requiredField, "column is-half")}>
                      {left.label?.id ? <label className={classnames('label', styles.customLabel)} htmlFor={left.name}>
                        {intl.formatMessage({ id: left.label.id, defaultMessage: left.label.defaultMessage })}
                      </label> : <></>}
                      <input onChange={updateData} name={left.name} required={left.required} className="input" type="text" placeholder={intl.formatMessage({ id: left.placeholder?.id, defaultMessage: left.placeholder?.defaultMessage })} />
                    </div>
                    {Boolean(right) && (
                      <div className={classnames(styles.requiredField, "column is-half")}>
                        {right.label?.id ? <label className={classnames('label', styles.customLabel)} htmlFor={right.name}>
                          {intl.formatMessage({ id: right.label.id, defaultMessage: right.label.defaultMessage })}
                        </label> : <></>}
                        <input onChange={updateData} name={right.name} className="input" type="text" required={right.required} placeholder={intl.formatMessage({ id: right.placeholder?.id, defaultMessage: right.placeholder?.defaultMessage })} />
                      </div>
                    )}
                  </div>
                ))}
                {textArea.name ?
                  <div>
                    <label className={classnames('label', styles.customLabel)} htmlFor="additional-comments">
                      {intl.formatMessage({ id: textArea.label?.id, defaultMessage: textArea.label?.defaultMessage })}
                    </label>
                    <textarea value={data[textArea?.name]} onChange={updateData} name={textArea?.name} className="textarea mb-4" placeholder={intl.formatMessage({ id: textArea.placeholder?.id, defaultMessage: textArea.placeholder?.defaultMessage })}></textarea>
                  </div> : <></>}
                {checkBox.name ? <div className={styles.checkbox}>
                  <input type={checkBox.type} onChange={updateData} id={checkBox.name} name={checkBox.name} value="yes" />
                  <br />
                  <label htmlFor="check" className={classnames('label', showFormInFooter ? styles.checkboxLabelFooter : styles.checkboxLabel)}>{intl.formatMessage({ id: checkBox.label?.id, defaultMessage: checkBox?.label?.defaultMessage })}</label><br></br>
                </div> : <></>}
              </div>
              <footer className={classnames(styles.footer, className)}>
                <button id='contact-form' name='submit' type='submit' disabled={showbutton} className="is-primary button has-text-white has-text-weight-bold">
                  <span id='contact-form'>{intl.formatMessage(submit.placeholder)}</span>
                  {hasRightToLeftLanguage ?
                    <ArrowLeft size={16} className={classnames(styles.buttonArrow, 'text-1 feather-align-right')} /> :
                    <ArrowRight size={16} className={classnames(styles.buttonArrow, 'text-1 feather-align-right')} />
                  }
                </button>
              </footer>
            </>
        }
      </div>
    </form>
  )
}

export default ContactForm;
